import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  Clock,
  ExternalLink,
  Mail,
  MessageCircle,
  Send,
  Sparkles,
  Users,
  X,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import {
  api,
  type DistributionChannelView,
  type DistributionRowView,
  type DistributionStatusView,
} from "../../lib/api.js";
import { StatCard } from "../../components/ui/StatCard.js";
import { Sparkline } from "../../components/ui/Sparkline.js";
import { SectionCard } from "../../components/ui/SectionCard.js";
import { EmptyState } from "../../components/ui/EmptyState.js";
import { Skeleton } from "../../components/ui/Skeleton.js";

/**
 * Distribution page — append-only feed of every send the platform has
 * performed. Powers a per-channel breakdown, filter chips, and a
 * detail-drawer for digging into one send (channel id, recipient, error
 * message, click-through to the article).
 *
 * Sends are logged from the ui-server's `/api/pipeline/keywords/:id/distribute`
 * route — both successes AND failures. The page therefore doubles as an
 * incident log: filter by `failed` to triage outages.
 */

export function DistributionPage() {
  const [channelFilter, setChannelFilter] = useState<DistributionChannelView | "all">("all");
  const [statusFilter, setStatusFilter] = useState<DistributionStatusView | "all">("all");
  const [selected, setSelected] = useState<DistributionRowView | null>(null);

  const overview = useQuery({
    queryKey: ["distribution", "overview"],
    queryFn: () => api.getDistributionOverview(),
    refetchInterval: 30_000,
  });

  const sends = useQuery({
    queryKey: ["distribution", "sends", { channel: channelFilter, status: statusFilter }],
    queryFn: () =>
      api.listDistributions({
        channel: channelFilter === "all" ? undefined : channelFilter,
        status: statusFilter === "all" ? undefined : statusFilter,
        limit: 200,
      }),
    refetchInterval: 30_000,
  });

  if (overview.error) {
    return (
      <div className="card p-6 text-sm text-danger">
        Failed to load distribution: {(overview.error as Error).message}
      </div>
    );
  }

  const kpis = overview.data?.kpis;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-ink">Distribution</h2>
        <p className="mt-1 text-sm text-muted">
          Every send the platform performs — emails, Telegram posts, future channels. Filter by channel or status to triage.
        </p>
      </div>

      {/* KPI strip */}
      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Total sends"
          value={overview.isPending ? "—" : (kpis?.totalSends ?? 0).toLocaleString()}
          icon={Send}
          tone="brand"
          hint={kpis ? `${kpis.last30dSends.toLocaleString()} in last 30 days` : undefined}
          sparkline={
            kpis?.sparkline30d && (
              <Sparkline data={kpis.sparkline30d} width={140} height={40} />
            )
          }
        />
        <StatCard
          label="Delivered"
          value={overview.isPending ? "—" : (kpis?.successCount ?? 0).toLocaleString()}
          icon={CheckCircle2}
          tone="accent"
          hint="status = sent"
        />
        <StatCard
          label="Scheduled"
          value={overview.isPending ? "—" : (kpis?.scheduledCount ?? 0).toLocaleString()}
          icon={CalendarClock}
          tone="warn"
          hint="upcoming campaigns"
        />
        <StatCard
          label="Failures"
          value={overview.isPending ? "—" : (kpis?.failedCount ?? 0).toLocaleString()}
          icon={XCircle}
          tone="danger"
          hint="needs operator triage"
        />
      </section>

      {/* Per-channel breakdown */}
      {kpis && Object.keys(kpis.byChannel).length > 0 && (
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {Object.entries(kpis.byChannel).map(([channel, count]) => {
            const meta = channelMeta(channel as DistributionChannelView);
            return (
              <button
                key={channel}
                onClick={() => setChannelFilter(channel as DistributionChannelView)}
                className={
                  channelFilter === channel
                    ? "card border-brand bg-brand-50 px-4 py-3 text-left ring-2 ring-brand/30"
                    : "card card-hover px-4 py-3 text-left"
                }
              >
                <div className="flex items-center gap-2">
                  <meta.Icon className={`h-4 w-4 ${meta.tintText}`} strokeWidth={2.2} />
                  <span className="text-xs font-semibold text-ink">{meta.label}</span>
                </div>
                <div className="mt-2 text-xl font-semibold text-ink">{count}</div>
              </button>
            );
          })}
        </section>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-1.5">
        <FilterChip active={channelFilter === "all"} onClick={() => setChannelFilter("all")}>All channels</FilterChip>
        {(["brevo", "telegram"] as DistributionChannelView[]).map((c) => {
          const meta = channelMeta(c);
          return (
            <FilterChip
              key={c}
              active={channelFilter === c}
              onClick={() => setChannelFilter(c)}
              icon={meta.Icon}
            >
              {meta.label}
            </FilterChip>
          );
        })}
        <div className="ml-auto flex items-center gap-1.5">
          <span className="text-2xs uppercase tracking-wider text-muted">Status</span>
          <FilterChip active={statusFilter === "all"} onClick={() => setStatusFilter("all")}>All</FilterChip>
          <FilterChip active={statusFilter === "sent"} onClick={() => setStatusFilter("sent")}>Sent</FilterChip>
          <FilterChip active={statusFilter === "scheduled"} onClick={() => setStatusFilter("scheduled")}>Scheduled</FilterChip>
          <FilterChip active={statusFilter === "failed"} onClick={() => setStatusFilter("failed")}>Failed</FilterChip>
        </div>
      </div>

      {/* Feed */}
      <SectionCard
        title="Send history"
        subtitle={sends.data ? `${sends.data.count} send${sends.data.count === 1 ? "" : "s"} match the current filters` : ""}
        icon={Send}
        padded={false}
      >
        {sends.isPending ? (
          <SendSkeleton />
        ) : sends.data && sends.data.sends.length > 0 ? (
          <ul className="divide-y divide-line">
            {sends.data.sends.map((s) => (
              <SendRow key={s.id} send={s} onSelect={() => setSelected(s)} />
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={Send}
            title="No sends yet"
            description="Send something from Pipeline → keyword detail → Distribute, or use the AI assistant to fire one for you. New sends appear here in real time."
          />
        )}
      </SectionCard>

      {selected && (
        <SendDetailDrawer send={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Channel + status display helpers
// ───────────────────────────────────────────────────────────────────────────

interface ChannelMeta {
  label: string;
  Icon: LucideIcon;
  tintBg: string;
  tintText: string;
}
function channelMeta(channel: DistributionChannelView): ChannelMeta {
  switch (channel) {
    case "brevo":     return { label: "Brevo",     Icon: Mail,          tintBg: "bg-info-soft",    tintText: "text-blue-700" };
    case "telegram":  return { label: "Telegram",  Icon: MessageCircle, tintBg: "bg-info-soft",    tintText: "text-blue-700" };
    case "whatsapp":  return { label: "WhatsApp",  Icon: MessageCircle, tintBg: "bg-accent-soft",  tintText: "text-emerald-700" };
    case "meta":      return { label: "FB / IG",   Icon: Sparkles,      tintBg: "bg-info-soft",    tintText: "text-blue-700" };
    case "x":         return { label: "X",         Icon: Sparkles,      tintBg: "bg-line-soft",    tintText: "text-muted-dark" };
    case "youtube":   return { label: "YouTube",   Icon: Sparkles,      tintBg: "bg-danger-soft",  tintText: "text-red-700" };
    case "tiktok":    return { label: "TikTok",    Icon: Sparkles,      tintBg: "bg-line-soft",    tintText: "text-muted-dark" };
    case "postiz":    return { label: "Postiz",    Icon: Send,          tintBg: "bg-accent-soft",  tintText: "text-emerald-700" };
  }
}

function StatusPill({ status }: { status: DistributionStatusView }) {
  switch (status) {
    case "sent":
      return <span className="pill-ok"><CheckCircle2 className="h-3 w-3" strokeWidth={2.4} />Sent</span>;
    case "scheduled":
      return <span className="pill-warn"><CalendarClock className="h-3 w-3" />Scheduled</span>;
    case "failed":
      return <span className="pill-danger"><AlertCircle className="h-3 w-3" />Failed</span>;
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Rows + detail drawer
// ───────────────────────────────────────────────────────────────────────────

function FilterChip({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon?: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        active
          ? "inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-3 py-1.5 text-xs font-medium text-white"
          : "inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-muted-dark ring-1 ring-line hover:bg-canvas hover:text-ink"
      }
    >
      {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />}
      {children}
    </button>
  );
}

function SendRow({ send, onSelect }: { send: DistributionRowView; onSelect: () => void }) {
  const meta = channelMeta(send.channel);
  const Icon = meta.Icon;
  const recipient = send.recipientEmail ?? (send.listId ? `list #${send.listId}` : send.chatId ? `chat ${send.chatId}` : "—");
  return (
    <li
      onClick={onSelect}
      className="cursor-pointer px-5 py-3.5 transition hover:bg-canvas/60"
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${meta.tintBg} ${meta.tintText}`}>
          <Icon className="h-4 w-4" strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-ink" dir={send.keyword?.language === "he" ? "rtl" : "ltr"}>
              {send.subject ?? send.keyword?.keyword ?? "(no subject)"}
            </span>
            <StatusPill status={send.status} />
          </div>
          {send.error && (
            <div className="mt-1 text-xs text-red-700">
              <AlertCircle className="mr-1 inline h-3 w-3" />
              {send.error.length > 140 ? send.error.slice(0, 137) + "…" : send.error}
            </div>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-muted">
            <span className="font-medium">{meta.label}</span>
            <span>·</span>
            <span className="capitalize">{send.kind.replace("_", " ")}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <Users className="h-3 w-3" />
              {send.recipientsCount.toLocaleString()} {send.recipientsCount === 1 ? "recipient" : "recipients"}
            </span>
            {send.keyword && (
              <>
                <span>·</span>
                <span className="truncate" dir={send.keyword.language === "he" ? "rtl" : "ltr"}>
                  {send.keyword.keyword}
                </span>
              </>
            )}
            <span className="ml-auto inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {relativeTime(send.sentAt)}
            </span>
          </div>
          <div className="mt-1 truncate text-2xs text-muted/70">
            {recipient}
            {send.messageId && <span className="ml-2 font-mono">{send.messageId.length > 40 ? send.messageId.slice(0, 37) + "…" : send.messageId}</span>}
          </div>
        </div>
      </div>
    </li>
  );
}

function SendSkeleton() {
  return (
    <ul className="divide-y divide-line">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="flex items-start gap-3 px-5 py-3.5">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-2 w-1/3" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function SendDetailDrawer({ send, onClose }: { send: DistributionRowView; onClose: () => void }) {
  const meta = channelMeta(send.channel);
  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/20 backdrop-blur-[1px] animate-fade-in" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col bg-white shadow-rail animate-slide-in-right">
        <header className="flex items-start justify-between gap-3 border-b border-line p-5">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${meta.tintBg} ${meta.tintText}`}>
              <meta.Icon className="h-5 w-5" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <StatusPill status={send.status} />
                <span className="text-2xs uppercase tracking-wider text-muted">
                  {meta.label} · {send.kind.replace("_", " ")}
                </span>
              </div>
              <h3 className="mt-1.5 truncate text-base font-semibold text-ink" dir={send.keyword?.language === "he" ? "rtl" : "ltr"}>
                {send.subject ?? send.keyword?.keyword ?? "(no subject)"}
              </h3>
            </div>
          </div>
          <button onClick={onClose} className="btn-icon shrink-0">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto p-5 scrollbar-thin">
          {send.error && (
            <div className="rounded-xl border border-danger/30 bg-danger-soft p-4">
              <div className="text-2xs font-semibold uppercase tracking-wider text-red-700">Failure reason</div>
              <pre className="mt-1.5 whitespace-pre-wrap break-words text-xs text-ink">{send.error}</pre>
            </div>
          )}

          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Field label="Sent at">{new Date(send.sentAt).toLocaleString("en-GB")}</Field>
            <Field label="Recipients">{send.recipientsCount.toLocaleString()}</Field>
            {send.recipientEmail && <Field label="Recipient">{send.recipientEmail}</Field>}
            {send.listId !== null && <Field label="List id">#{send.listId}</Field>}
            {send.chatId && <Field label="Chat / channel">{send.chatId}</Field>}
            {send.messageId && <Field label="Message id" wide><code className="break-all font-mono text-xs">{send.messageId}</code></Field>}
            {send.campaignId && <Field label="Campaign id"><code className="font-mono text-xs">{send.campaignId}</code></Field>}
            {send.assetType && <Field label="Asset">{send.assetType}{send.assetPath ? ` — ${send.assetPath.split(/[/\\]/).pop()}` : ""}</Field>}
            {send.htmlBytes !== null && <Field label="HTML size">{(send.htmlBytes / 1024).toFixed(1)} KB</Field>}
            {send.scheduledAt && <Field label="Scheduled for">{new Date(send.scheduledAt).toLocaleString("en-GB")}</Field>}
          </dl>

          {send.keyword && (
            <div className="rounded-lg border border-line bg-canvas-deep/40 p-3">
              <div className="text-2xs font-semibold uppercase tracking-wider text-muted">Linked keyword</div>
              <div className="mt-1 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium" dir={send.keyword.language === "he" ? "rtl" : "ltr"}>{send.keyword.keyword}</div>
                  <div className="truncate text-2xs text-muted">{send.keyword.id}{send.keyword.cluster && ` · ${send.keyword.cluster}`}</div>
                </div>
                <a
                  href={`#/pipeline?open=${encodeURIComponent(send.keyword.id)}`}
                  className="btn-secondary btn-sm shrink-0"
                >
                  Open
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          )}

          {send.url && (
            <div className="rounded-lg border border-line bg-canvas-deep/40 p-3">
              <div className="text-2xs font-semibold uppercase tracking-wider text-muted">Article URL</div>
              <a
                href={send.url}
                target="_blank"
                rel="noreferrer"
                className="mt-1 block truncate text-xs text-brand-700 hover:underline"
              >
                {send.url}
              </a>
            </div>
          )}

          {send.metadata && Object.keys(send.metadata).length > 0 && (
            <details className="rounded-lg border border-line bg-canvas-deep/40 p-3">
              <summary className="cursor-pointer text-2xs font-semibold uppercase tracking-wider text-muted">Raw metadata</summary>
              <pre className="mt-2 overflow-x-auto text-2xs text-ink">{JSON.stringify(send.metadata, null, 2)}</pre>
            </details>
          )}
        </div>
      </aside>
    </>
  );
}

function Field({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? "col-span-2" : undefined}>
      <dt className="text-2xs font-semibold uppercase tracking-wider text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  );
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { month: "short", day: "numeric" });
}
